import { z } from "zod"

/*
 * ⚠ One boolean used to decide two unrelated things. `elevated` gated both "may read the real
 * identities behind the pseudonyms" and "may create, offboard and purge a customer" — a privacy
 * capability and an irreversible destructive one. Customer Success asked for the first (the
 * per-user detail metrics exist because they did); nobody intended that to carry the second.
 * Roles exist to separate them, and `purge` is separated again from the rest of deployment
 * management because it is the only action with no way back.
 */

export const ROLES = ["viewer", "analyst", "admin"] as const
export const roleSchema = z.enum(ROLES)
export type Role = (typeof ROLES)[number]

export const CAPABILITIES = ["read", "identities", "deployments", "purge", "staff"] as const
export type Capability = (typeof CAPABILITIES)[number]

const BY_ROLE: Readonly<Record<Role, readonly Capability[]>> = {
  viewer: ["read"],
  analyst: ["read", "identities"],
  admin: ["read", "identities", "deployments", "purge", "staff"],
}

export const can = (role: Role, capability: Capability): boolean => BY_ROLE[role].includes(capability)

export const capabilitiesOf = (role: Role): readonly Capability[] => BY_ROLE[role]

/** `tenant_scope` stays orthogonal: it narrows which customers a role applies to, never which capabilities */
export const DEFAULT_ROLE: Role = "viewer"

export const ROLE_LABELS: Readonly<Record<Role, string>> = {
  viewer: "Viewer",
  analyst: "Analyst",
  admin: "Administrator",
}

export const ROLE_DESCRIPTIONS: Readonly<Record<Role, string>> = {
  viewer: "Dashboards, metrics and collection health. Pseudonyms are not resolved to names.",
  analyst: "Everything a viewer sees, plus the metrics that return real identities.",
  admin: "Everything, plus managing deployments, purging a customer's data, and managing staff.",
}

/** The message a refused request carries. Names the capability, not the role, so it stays true when roles change */
export const CAPABILITY_DENIALS: Readonly<Record<Capability, string>> = {
  read: "This action needs an active account.",
  identities: "Resolving pseudonyms to real people needs the Analyst or Administrator role.",
  deployments: "Managing deployments needs the Administrator role.",
  purge: "Purging a customer's data needs the Administrator role.",
  staff: "Managing staff needs the Administrator role.",
}

/** A role that is not one of ours is treated as the least privileged, never as the most */
export const asRole = (value: string): Role => (ROLES as readonly string[]).includes(value) ? (value as Role) : DEFAULT_ROLE
